package features.booking

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import kotlinx.coroutines.launch

@Composable
fun ConfigPagosView(viewModel: AdminViewModel, miRolActual: String = "admin") {
    val stripeActivo by viewModel.stripeActivo.collectAsState()
    val clabeManual by viewModel.clabeManual.collectAsState()
    val beneficiarioManual by viewModel.beneficiarioManual.collectAsState()
    val bancoManual by viewModel.bancoManual.collectAsState()
    val telefonoAtencion by viewModel.telefonoAtencion.collectAsState()

    // GPS y modo prueba también se editan en borrador y se aplican al guardar. Antes se
    // escribían al instante y el estado quedaba inconsistente si la escritura fallaba o
    // llegaba tarde: ahora nada cambia hasta que se confirma el guardado.
    val gpsChoferesActivo by viewModel.gpsChoferesActivo.collectAsState()
    val modoPruebaActivo by network.ModoPruebaPagos.activo.collectAsState()

    var editStripe by remember(stripeActivo) { mutableStateOf(stripeActivo) }
    var editClabe by remember(clabeManual) { mutableStateOf(clabeManual) }
    var editBeneficiario by remember(beneficiarioManual) { mutableStateOf(beneficiarioManual) }
    var editBanco by remember(bancoManual) { mutableStateOf(bancoManual) }
    var editTelefono by remember(telefonoAtencion) { mutableStateOf(telefonoAtencion) }
    var editGps by remember(gpsChoferesActivo) { mutableStateOf(gpsChoferesActivo) }
    var editModoPrueba by remember(modoPruebaActivo) { mutableStateOf(modoPruebaActivo) }

    var guardando by remember { mutableStateOf(false) }
    var msgGuardado by remember { mutableStateOf("") }
    val scopeGuardar = rememberCoroutineScope()

    val hayCambios = editStripe != stripeActivo || editClabe != clabeManual ||
            editBeneficiario != beneficiarioManual || editBanco != bancoManual || editTelefono != telefonoAtencion ||
            editGps != gpsChoferesActivo || editModoPrueba != modoPruebaActivo

    // Pagos manuales pendientes
    val pagosManuales by viewModel.pagosManuales.collectAsState()
    val cargandoPagos by viewModel.cargandoPagosManuales.collectAsState()
    var pagoAConfirmar by remember { mutableStateOf<PagoManual?>(null) }
    var pagoADeclinar by remember { mutableStateOf<PagoManual?>(null) }
    var procesando by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) { viewModel.cargarPagosManuales() }

    Column(
        modifier = Modifier.fillMaxSize().padding(16.dp).verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Text("Configuración de Pagos", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
        Text("Controla cómo se procesan los pagos por transferencia.", color = Color.Gray, style = MaterialTheme.typography.bodySmall)

        // ═══ MODO PRUEBA (SANDBOX) — master/desarrollador/admin_general ═══
        // Permite probar el flujo de pago con tarjetas de test (4242…) sin mover dinero real,
        // mientras los clientes siguen comprando en live. El servidor valida el rol otra vez
        // (permiteModoPrueba): si alguien más manda modoPrueba, se ignora y cobra en live.
        // admin_general incluido temporalmente para validar el flujo antes de liberar.
        if (miRolActual == "desarrollador" || miRolActual == "master" || miRolActual == "admin_general") {
            Card(
                colors = CardDefaults.cardColors(containerColor = if (editModoPrueba) Color(0xFFFFF3E0) else Color(0xFFF5F5F5)),
                shape = RoundedCornerShape(16.dp),
                elevation = CardDefaults.cardElevation(2.dp)
            ) {
                Column(modifier = Modifier.padding(20.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            if (editModoPrueba) Icons.Filled.Science else Icons.Filled.CreditCard,
                            contentDescription = null,
                            tint = if (editModoPrueba) SmartVanColors.NaranjaDeep else Color.Gray
                        )
                        Spacer(modifier = Modifier.width(10.dp))
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                if (editModoPrueba) "MODO PRUEBA ACTIVO" else "Modo prueba (sandbox)",
                                fontWeight = FontWeight.Black,
                                color = if (editModoPrueba) SmartVanColors.NaranjaDeep else SmartVanColors.AzulOscuro
                            )
                            Text(
                                if (editModoPrueba) "Tus compras NO cobran dinero real. Usa la tarjeta 4242 4242 4242 4242."
                                else "Actívalo para probar pagos sin cobrar dinero real. Solo afecta a tu sesión.",
                                color = Color.Gray, fontSize = 12.sp
                            )
                        }
                        Switch(
                            checked = editModoPrueba,
                            onCheckedChange = { editModoPrueba = it }
                        )
                    }
                    if (editModoPrueba) {
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            "Ojo: el asiento se ocupa de verdad y el boleto se envía. Usa viajes de prueba, " +
                                "no los que estás vendiendo. Queda encendido hasta que lo apagues.",
                            color = SmartVanColors.NaranjaDeep, fontSize = 11.sp
                        )
                    }
                }
            }
        }

        // ═══ SWITCH STRIPE ═══
        Card(
            colors = CardDefaults.cardColors(containerColor = if (editStripe) Color(0xFFE8F5E9) else SmartVanColors.FondoAmarillo),
            shape = RoundedCornerShape(16.dp),
            elevation = CardDefaults.cardElevation(2.dp)
        ) {
            Column(modifier = Modifier.padding(20.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            modifier = Modifier.size(44.dp).background(
                                if (editStripe) SmartVanColors.Verde.copy(alpha = 0.15f) else SmartVanColors.Naranja.copy(alpha = 0.15f),
                                CircleShape
                            ),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                if (editStripe) Icons.Filled.CheckCircle else Icons.Filled.Warning,
                                contentDescription = null,
                                tint = if (editStripe) SmartVanColors.Verde else SmartVanColors.Naranja,
                                modifier = Modifier.size(24.dp)
                            )
                        }
                        Spacer(modifier = Modifier.width(14.dp))
                        Column {
                            Text(
                                if (editStripe) "Stripe ACTIVO" else "Stripe DESACTIVADO",
                                fontWeight = FontWeight.Black,
                                color = if (editStripe) SmartVanColors.VerdeOscuro else Color(0xFF6D4C00),
                                style = MaterialTheme.typography.titleMedium
                            )
                            Text(
                                if (editStripe) "Pagos con CLABE dinámica de Stripe + verificación automática"
                                else "Pagos con tu CLABE propia — verificación manual",
                                style = MaterialTheme.typography.bodySmall,
                                color = if (editStripe) SmartVanColors.VerdeOscuro else Color(0xFF6D4C00)
                            )
                        }
                    }
                    Switch(
                        checked = editStripe,
                        onCheckedChange = { editStripe = it },
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = SmartVanColors.Verde,
                            checkedTrackColor = SmartVanColors.Verde.copy(alpha = 0.3f),
                            uncheckedThumbColor = SmartVanColors.Naranja,
                            uncheckedTrackColor = SmartVanColors.Naranja.copy(alpha = 0.3f)
                        )
                    )
                }

                if (!editStripe) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Card(
                        colors = CardDefaults.cardColors(containerColor = Color(0xFFFFF8E1)),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Filled.Info, contentDescription = null, tint = Color(0xFFF57F17), modifier = Modifier.size(16.dp))
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                "Sin Stripe: no hay comisión pero los pagos se verifican manualmente. Los clientes verán tu CLABE y un mensaje para enviar comprobante.",
                                style = MaterialTheme.typography.labelSmall,
                                color = Color(0xFF6D4C00)
                            )
                        }
                    }
                }
            }
        }

        // ═══ GPS DE CHOFERES (control de costos) ═══
        val gpsActivo by viewModel.gpsChoferesActivo.collectAsState()
        Card(
            colors = CardDefaults.cardColors(containerColor = Color.White),
            shape = RoundedCornerShape(16.dp),
            elevation = CardDefaults.cardElevation(2.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(20.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        if (gpsActivo) "GPS de choferes ACTIVO" else "GPS de choferes DESACTIVADO",
                        fontWeight = FontWeight.Black,
                        color = if (gpsActivo) SmartVanColors.VerdeOscuro else Color(0xFF6D4C00),
                        style = MaterialTheme.typography.titleMedium
                    )
                    Text(
                        if (gpsActivo) "El chofer transmite ubicación en vivo durante el viaje."
                        else "Sin ubicación en vivo. Ahorra escrituras de Firebase (usar con GPS físicos).",
                        style = MaterialTheme.typography.bodySmall,
                        color = Color.Gray
                    )
                }
                Spacer(modifier = Modifier.width(12.dp))
                Switch(
                    checked = editGps,
                    onCheckedChange = { editGps = it },
                    colors = SwitchDefaults.colors(
                        checkedThumbColor = SmartVanColors.Verde,
                        checkedTrackColor = SmartVanColors.Verde.copy(alpha = 0.3f),
                        uncheckedThumbColor = SmartVanColors.Naranja,
                        uncheckedTrackColor = SmartVanColors.Naranja.copy(alpha = 0.3f)
                    )
                )
            }
        }

        // ═══ DATOS BANCARIOS MANUALES ═══
        if (!editStripe) {
            Card(
                colors = CardDefaults.cardColors(containerColor = Color.White),
                shape = RoundedCornerShape(16.dp),
                elevation = CardDefaults.cardElevation(2.dp)
            ) {
                Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            modifier = Modifier.size(32.dp).background(SmartVanColors.CyanAccent.copy(alpha = 0.1f), CircleShape),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(Icons.Filled.AccountBalance, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(18.dp))
                        }
                        Spacer(modifier = Modifier.width(10.dp))
                        Text("Datos Bancarios", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    }

                    OutlinedTextField(
                        value = editClabe,
                        onValueChange = { if (it.length <= 18 && it.all { c -> c.isDigit() }) editClabe = it },
                        label = { Text("CLABE Interbancaria") },
                        placeholder = { Text("18 dígitos") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp),
                        supportingText = { Text("${editClabe.length}/18 dígitos", color = if (editClabe.length == 18) SmartVanColors.Verde else Color.Gray) }
                    )

                    OutlinedTextField(
                        value = editBeneficiario,
                        onValueChange = { editBeneficiario = it },
                        label = { Text("Beneficiario") },
                        placeholder = { Text("Nombre del titular de la cuenta") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp)
                    )

                    OutlinedTextField(
                        value = editBanco,
                        onValueChange = { editBanco = it },
                        label = { Text("Banco") },
                        placeholder = { Text("BBVA, Banorte, etc.") },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp)
                    )
                }
            }
        }

        // ═══ TELÉFONO DE ATENCIÓN ═══
        Card(
            colors = CardDefaults.cardColors(containerColor = Color.White),
            shape = RoundedCornerShape(16.dp),
            elevation = CardDefaults.cardElevation(2.dp)
        ) {
            Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier.size(32.dp).background(SmartVanColors.Verde.copy(alpha = 0.1f), CircleShape),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(Icons.Filled.Phone, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(18.dp))
                    }
                    Spacer(modifier = Modifier.width(10.dp))
                    Column {
                        Text("Teléfono de Atención", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                        Text("Se muestra al cliente para enviar comprobante de pago", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                    }
                }

                OutlinedTextField(
                    value = editTelefono,
                    onValueChange = { if (it.length <= 15) editTelefono = it },
                    label = { Text("WhatsApp / Teléfono") },
                    placeholder = { Text("5512345678") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp)
                )
            }
        }

        // ═══ BOTÓN GUARDAR ═══
        // Aplica TODO junto (Stripe, GPS, modo prueba y datos de transferencia) y luego
        // relee del servidor para confirmar que quedó guardado. Sin esa relectura, un fallo
        // de escritura dejaba la pantalla mostrando un valor que en realidad no se aplicó.
        if (hayCambios || msgGuardado.isNotBlank()) {
            Button(
                onClick = {
                    scopeGuardar.launch {
                        guardando = true; msgGuardado = ""
                        viewModel.guardarConfigPagos(editStripe, editClabe, editBeneficiario, editBanco, editTelefono)
                        network.ConfigPagos.actualizar(editStripe, editClabe, editBeneficiario, editBanco, editTelefono)
                        if (editGps != gpsChoferesActivo) viewModel.setGpsChoferesActivo(editGps)
                        if (editModoPrueba != modoPruebaActivo) network.ModoPruebaPagos.activar(editModoPrueba)
                        // Releer del servidor: confirma que se aplicó de verdad.
                        val ok = viewModel.verificarConfigAplicada(editStripe, editGps)
                        msgGuardado = if (ok) "Cambios guardados y aplicados."
                            else "No se pudo confirmar el guardado. Revisa tu conexión y vuelve a intentar."
                        guardando = false
                    }
                },
                enabled = !guardando && hayCambios,
                modifier = Modifier.fillMaxWidth().height(50.dp),
                colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro),
                shape = RoundedCornerShape(14.dp)
            ) {
                if (guardando) {
                    CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp, color = Color.White)
                } else {
                    Icon(Icons.Filled.CheckCircle, contentDescription = null, modifier = Modifier.size(20.dp))
                }
                Spacer(modifier = Modifier.width(8.dp))
                Text(if (guardando) "Guardando…" else "Guardar Configuración", fontWeight = FontWeight.Bold)
            }
            if (msgGuardado.isNotBlank()) {
                Text(
                    msgGuardado,
                    color = if (msgGuardado.startsWith("Cambios")) SmartVanColors.VerdeOscuro else Color(0xFFC62828),
                    style = MaterialTheme.typography.bodySmall,
                    fontWeight = FontWeight.SemiBold
                )
            }
        }

        // ═══ PAGOS MANUALES PENDIENTES ═══
        if (!stripeActivo) {
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp), color = SmartVanColors.Borde)

            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Filled.Receipt, contentDescription = null, tint = SmartVanColors.Naranja, modifier = Modifier.size(24.dp))
                Spacer(modifier = Modifier.width(8.dp))
                Text("Pagos Pendientes de Verificar", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.titleMedium)
                Spacer(modifier = Modifier.weight(1f))
                IconButton(onClick = { viewModel.cargarPagosManuales() }) {
                    Icon(Icons.Filled.Refresh, contentDescription = "Actualizar", tint = SmartVanColors.CyanAccent)
                }
            }
            Text("Transferencias sin Stripe que requieren tu confirmación manual.", color = Color.Gray, style = MaterialTheme.typography.bodySmall)

            if (cargandoPagos) {
                Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = SmartVanColors.AzulOscuro)
                }
            } else if (pagosManuales.isEmpty()) {
                Card(
                    colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoGrisClaro),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(24.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(40.dp))
                        Spacer(modifier = Modifier.height(8.dp))
                        Text("No hay pagos pendientes", color = Color.Gray, fontWeight = FontWeight.Medium)
                    }
                }
            } else {
                Text("${pagosManuales.count { it.estatus == "pendiente" }} pago(s) pendiente(s)", color = SmartVanColors.Naranja, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                Spacer(modifier = Modifier.height(4.dp))

                pagosManuales.forEach { pago ->
                    Card(
                        colors = CardDefaults.cardColors(containerColor = Color.White),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                        elevation = CardDefaults.cardElevation(2.dp)
                    ) {
                        val (estIcon, estLabel, estColor) = when (pago.estatus) {
                            "confirmado" -> Triple(Icons.Filled.CheckCircle, "CONFIRMADO", SmartVanColors.Verde)
                            "declinado" -> Triple(Icons.Filled.Cancel, "DECLINADO", Color.Red)
                            "expirado" -> Triple(Icons.Filled.HourglassEmpty, "EXPIRADO", Color.Gray)
                            else -> Triple(Icons.Filled.HourglassEmpty, "PENDIENTE", SmartVanColors.Naranja)
                        }
                        Column(modifier = Modifier.padding(14.dp)) {
                            // Header
                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                Icon(estIcon, contentDescription = null, tint = estColor, modifier = Modifier.size(18.dp))
                                Spacer(modifier = Modifier.width(6.dp))
                                Text(estLabel, fontWeight = FontWeight.Bold, color = estColor, style = MaterialTheme.typography.labelSmall)
                                Spacer(modifier = Modifier.weight(1f))
                                Text("$${pago.totalPagado} MXN", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
                            }
                            Spacer(modifier = Modifier.height(6.dp))

                            // Info del viaje
                            Text(pago.ruta, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodyMedium)
                            Text("${pago.fecha} • ${pago.horario}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                            Text("Asientos: ${pago.asientos}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                            if (pago.email.isNotBlank()) {
                                Text("Correo: ${pago.email}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                            }

                            // Pasajeros
                            if (pago.pasajeros.isNotBlank()) {
                                val nombresParseados = remember(pago.pasajeros) {
                                    try {
                                        val arr = kotlinx.serialization.json.Json.parseToJsonElement(pago.pasajeros)
                                        if (arr is kotlinx.serialization.json.JsonArray) {
                                            arr.mapNotNull { p ->
                                                (p as? kotlinx.serialization.json.JsonObject)?.get("nombre")?.toString()?.trim('"')?.ifBlank { null }
                                            }
                                        } else emptyList()
                                    } catch (_: Exception) { emptyList() }
                                }
                                nombresParseados.forEach { nombre ->
                                    Text("• $nombre", style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                                }
                            }

                            // Tiempo transcurrido
                            val ahora = org.example.abigail.obtenerMilisegundosActuales()
                            val minutos = ((ahora - pago.timestamp) / 60000).toInt()
                            val tiempoTexto = when {
                                minutos < 1 -> "Hace un momento"
                                minutos < 60 -> "Hace ${minutos}min"
                                minutos < 1440 -> "Hace ${minutos / 60}h"
                                else -> "Hace ${minutos / 1440}d"
                            }
                            Text(tiempoTexto, color = Color.LightGray, style = MaterialTheme.typography.labelSmall)

                            Spacer(modifier = Modifier.height(10.dp))

                            if (pago.estatus == "pendiente") {
                                // Botones (solo si está pendiente)
                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    Button(
                                        onClick = { pagoAConfirmar = pago },
                                        modifier = Modifier.weight(1f),
                                        colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde),
                                        shape = RoundedCornerShape(10.dp)
                                    ) {
                                        Icon(Icons.Filled.CheckCircle, contentDescription = null, modifier = Modifier.size(16.dp))
                                        Spacer(modifier = Modifier.width(4.dp))
                                        Text("Confirmar", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                                    }
                                    OutlinedButton(
                                        onClick = { pagoADeclinar = pago },
                                        modifier = Modifier.weight(1f),
                                        shape = RoundedCornerShape(10.dp),
                                        colors = ButtonDefaults.outlinedButtonColors(contentColor = Color.Red),
                                        border = androidx.compose.foundation.BorderStroke(1.dp, Color.Red)
                                    ) {
                                        Icon(Icons.Filled.Cancel, contentDescription = null, modifier = Modifier.size(16.dp), tint = Color.Red)
                                        Spacer(modifier = Modifier.width(4.dp))
                                        Text("Declinar", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color.Red)
                                    }
                                }
                            } else {
                                // Ya resuelto: mostrar estado + quién, sin botones
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier.fillMaxWidth().background(estColor.copy(alpha = 0.10f), RoundedCornerShape(10.dp)).padding(10.dp)
                                ) {
                                    Icon(estIcon, contentDescription = null, tint = estColor, modifier = Modifier.size(16.dp))
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text(
                                        when (pago.estatus) {
                                            "confirmado" -> "Confirmado por ${pago.resueltoPorNombre.ifBlank { "operador" }}"
                                            "declinado" -> "Declinado por ${pago.resueltoPorNombre.ifBlank { "operador" }}"
                                            "expirado" -> "Expiró el plazo de pago (asientos liberados)"
                                            else -> pago.estatus
                                        },
                                        color = estColor, fontWeight = FontWeight.SemiBold, style = MaterialTheme.typography.bodySmall
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // ═══ DIÁLOGO CONFIRMAR PAGO ═══
    pagoAConfirmar?.let { pago ->
        AlertDialog(
            onDismissRequest = { if (!procesando) pagoAConfirmar = null },
            icon = { Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(32.dp)) },
            title = { Text("Confirmar Pago", fontWeight = FontWeight.Bold, color = SmartVanColors.VerdeOscuro) },
            text = {
                Column {
                    Card(
                        colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Column(modifier = Modifier.padding(12.dp)) {
                            Text(pago.ruta, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                            Text("${pago.fecha} • ${pago.horario}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                            Text("Total: $${pago.totalPagado} MXN", fontWeight = FontWeight.Bold, color = SmartVanColors.Verde)
                        }
                    }
                    Spacer(modifier = Modifier.height(12.dp))
                    Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Filled.Warning, contentDescription = null, tint = Color.Red, modifier = Modifier.size(16.dp))
                            Spacer(modifier = Modifier.width(8.dp))
                            Text("Esta acción no puede revertirse. Se enviarán los boletos al cliente automáticamente.",
                                color = Color.Red, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Medium)
                        }
                    }
                    if (procesando) {
                        Spacer(modifier = Modifier.height(8.dp))
                        Row(horizontalArrangement = Arrangement.Center, modifier = Modifier.fillMaxWidth()) {
                            CircularProgressIndicator(color = SmartVanColors.Verde, modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                            Spacer(modifier = Modifier.width(8.dp))
                            Text("Procesando...", color = Color.Gray)
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        procesando = true
                        viewModel.confirmarPagoManual(pago) {
                            procesando = false
                            pagoAConfirmar = null
                        }
                    },
                    enabled = !procesando,
                    colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde)
                ) { Text("Sí, Confirmar Pago", fontWeight = FontWeight.Bold) }
            },
            dismissButton = {
                if (!procesando) TextButton(onClick = { pagoAConfirmar = null }) { Text("Cancelar") }
            }
        )
    }

    // ═══ DIÁLOGO DECLINAR PAGO ═══
    pagoADeclinar?.let { pago ->
        AlertDialog(
            onDismissRequest = { if (!procesando) pagoADeclinar = null },
            icon = { Icon(Icons.Filled.Cancel, contentDescription = null, tint = Color.Red, modifier = Modifier.size(32.dp)) },
            title = { Text("Declinar Pago", fontWeight = FontWeight.Bold, color = Color.Red) },
            text = {
                Column {
                    Card(
                        colors = CardDefaults.cardColors(containerColor = Color(0xFFFFF5F5)),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Column(modifier = Modifier.padding(12.dp)) {
                            Text(pago.ruta, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                            Text("${pago.fecha} • ${pago.horario}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                            Text("Asientos: ${pago.asientos}", color = Color.Gray, style = MaterialTheme.typography.bodySmall)
                        }
                    }
                    Spacer(modifier = Modifier.height(12.dp))
                    Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Filled.Warning, contentDescription = null, tint = Color.Red, modifier = Modifier.size(16.dp))
                            Spacer(modifier = Modifier.width(8.dp))
                            Text("Esta acción no puede revertirse. Los asientos serán liberados y el boleto no será procesado.",
                                color = Color.Red, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Medium)
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        procesando = true
                        viewModel.declinarPagoManual(pago) {
                            procesando = false
                            pagoADeclinar = null
                        }
                    },
                    enabled = !procesando,
                    colors = ButtonDefaults.buttonColors(containerColor = Color.Red)
                ) { Text("Sí, Declinar", fontWeight = FontWeight.Bold) }
            },
            dismissButton = {
                if (!procesando) TextButton(onClick = { pagoADeclinar = null }) { Text("Cancelar") }
            }
        )
    }
}
